import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Fragment, useState } from "react";
import { toast } from "sonner";
import {
  listContactRequests,
  updateContactRequest,
} from "@/lib/contact-requests.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/contact-requests")({
  head: () => ({
    meta: [
      { title: "Solicitudes de contacto · Administración" },
      {
        name: "description",
        content:
          "Consulta, filtra por folio o fecha y actualiza el estado de las solicitudes enviadas desde el formulario de contacto.",
      },
      { property: "og:title", content: "Solicitudes de contacto · Administración" },
      {
        property: "og:description",
        content: "Seguimiento interno de las solicitudes del formulario de contacto.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ContactRequestsPage,
});

const STATUS_LABEL: Record<string, string> = {
  new: "Nueva",
  in_progress: "En proceso",
  answered: "Respondida",
  closed: "Cerrada",
  spam: "Spam",
};

const STATUS_STYLE: Record<string, string> = {
  new: "bg-primary/10 text-primary border-primary/30",
  in_progress: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  answered: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  closed: "bg-muted text-muted-foreground border-border",
  spam: "bg-destructive/10 text-destructive border-destructive/30",
};

function ContactRequestsPage() {
  const list = useServerFn(listContactRequests);
  const update = useServerFn(updateContactRequest);
  const qc = useQueryClient();

  const [reference, setReference] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const filters = { reference, search, status, from, to };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin-contact-requests", filters],
    queryFn: () =>
      list({
        data: {
          reference: reference.trim() || undefined,
          search: search.trim() || undefined,
          status: status as never,
          from: from || undefined,
          to: to || undefined,
          limit: 300,
        },
      }),
  });

  const mut = useMutation({
    mutationFn: (input: { id: string; status?: string; adminNotes?: string }) =>
      update({ data: input as never }),
    onSuccess: () => {
      toast.success("Solicitud actualizada");
      qc.invalidateQueries({ queryKey: ["admin-contact-requests"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = data?.rows ?? [];
  const counts = data?.counts ?? {};

  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-2xl font-semibold">Solicitudes de contacto</h1>
        <p className="text-muted-foreground text-sm">
          Cada solicitud enviada desde el formulario del sitio queda registrada con su folio.
          Puedes buscarla, cambiar su estado y dejar notas internas de seguimiento.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.keys(STATUS_LABEL).map((s) => (
          <Badge key={s} variant="outline" className={STATUS_STYLE[s]}>
            {STATUS_LABEL[s]}: {counts[s] ?? 0}
          </Badge>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span>Folio</span>
            <Input
              className="w-40"
              placeholder="CT-41EA71"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span>Buscar (nombre, correo, asunto)</span>
            <Input
              className="w-64"
              placeholder="María o maria@correo.com"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span>Estado</span>
            <select
              className="border-input bg-background h-9 rounded-md border px-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="all">Todos</option>
              {Object.entries(STATUS_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span>Desde</span>
            <Input
              type="date"
              className="w-40"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span>Hasta</span>
            <Input
              type="date"
              className="w-40"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <Button
            variant="outline"
            onClick={() => {
              setReference("");
              setSearch("");
              setStatus("all");
              setFrom("");
              setTo("");
            }}
          >
            Limpiar
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Resultados ({rows.length}){isFetching ? " · actualizando…" : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground text-sm">Cargando…</p>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No hay solicitudes que coincidan con los filtros.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="py-2">Folio</th>
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Nombre</th>
                    <th className="py-2">Contacto</th>
                    <th className="py-2">Asunto</th>
                    <th className="py-2">Estado</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <Fragment key={r.id}>
                      <tr className="border-border/50 border-t align-top">
                        <td className="py-2 font-mono text-xs">{r.reference}</td>
                        <td className="py-2 whitespace-nowrap">
                          {new Date(r.created_at).toLocaleString("es-MX")}
                        </td>
                        <td className="py-2">{r.name}</td>
                        <td className="py-2">
                          <a className="text-primary hover:underline" href={`mailto:${r.email}`}>
                            {r.email}
                          </a>
                          {r.phone ? (
                            <div className="text-muted-foreground text-xs">{r.phone}</div>
                          ) : null}
                        </td>
                        <td className="py-2 max-w-64 truncate">{r.subject ?? "—"}</td>
                        <td className="py-2">
                          <select
                            className="border-input bg-background h-8 rounded-md border px-2 text-xs"
                            value={r.status}
                            onChange={(e) =>
                              mut.mutate({ id: r.id, status: e.target.value })
                            }
                          >
                            {Object.entries(STATUS_LABEL).map(([v, l]) => (
                              <option key={v} value={v}>
                                {l}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setOpen(open === r.id ? null : r.id)}
                          >
                            {open === r.id ? "Ocultar" : "Ver"}
                          </Button>
                        </td>
                      </tr>
                      {open === r.id ? (
                        <tr className="border-border/50 border-t">
                          <td colSpan={7} className="bg-muted/30 space-y-3 px-3 py-4">
                            <div>
                              <p className="text-muted-foreground text-xs uppercase">Mensaje</p>
                              <p className="whitespace-pre-wrap">{r.message}</p>
                            </div>
                            <div className="text-muted-foreground text-xs">
                              Idioma: {r.lang ?? "—"} · Origen: {r.source ?? "—"}
                            </div>
                            <div className="space-y-2">
                              <p className="text-muted-foreground text-xs uppercase">
                                Notas internas
                              </p>
                              <Textarea
                                defaultValue={r.admin_notes ?? ""}
                                rows={3}
                                onBlur={(e) => {
                                  const v = e.target.value;
                                  if (v !== (r.admin_notes ?? ""))
                                    mut.mutate({ id: r.id, adminNotes: v });
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
